/**
 * MyMouvMyTickets — user's tickets page (Profile → Mes commandes → Billets MyMouv).
 * Tickets auto-hide 7 days after the event date (server-side).
 *
 * v50.2 — QR codes are now clickable and open in a fullscreen viewer so
 * event staff can easily scan them at the door.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Ticket, Calendar, Loader2, X, Maximize2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const currencySymbol = (c) => ({ EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', JPY: '¥', CNY: '¥', INR: '₹', BRL: 'R$', RUB: '₽', KRW: '₩', ARS: '$', XOF: 'FCFA', XAF: 'FCFA' }[c] || c);

const MyMouvMyTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrFullscreen, setQrFullscreen] = useState(null); // {src, ticket}
  const navigate = useNavigate();
  const { t: tr } = useLanguage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/mymouv/my-tickets');
      setTickets(r.data?.tickets || []);
    } catch (_e) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mymouv-my-tickets">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-2xl font-bold">{tr('mymouvTickets.title', 'Mes billets MyMouv')}</h1>
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <Ticket className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{tr('mymouvTickets.empty', 'Aucun billet actif.')}</p>
            <button onClick={() => navigate('/mymouv')} className="mt-4 px-4 py-2 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-sm">{tr('mymouvTickets.discover', 'Découvrir des événements')}</button>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map(t => (
              <div key={t.id} className={`rounded-3xl p-5 border ${t.is_vip ? 'border-yellow-400/50 bg-gradient-to-br from-yellow-400/10 to-orange-500/10' : 'border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-900/30 to-purple-900/20'}`} data-testid={`mymouv-my-ticket-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">{t.ticket_type_name}{t.is_vip && ' · VIP'}</div>
                    <h3 className="text-lg font-bold mt-1">{t.event_title}</h3>
                    <div className="mt-2 text-sm text-white/70 flex items-center gap-2"><Calendar className="w-4 h-4" /> {t.event_date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/50">{tr('mymouvTickets.price', 'Prix')}</div>
                    <div className="font-bold">{t.price}{currencySymbol(t.currency)}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setQrFullscreen({ src: t.qr_data_url, ticket: t })}
                    className="relative group flex-shrink-0 rounded-xl"
                    aria-label={tr('mymouvTickets.enlargeQr', 'Agrandir le QR code')}
                    data-testid={`mymouv-ticket-qr-btn-${t.id}`}
                  >
                    <img src={t.qr_data_url} alt="QR" className="w-32 h-32 bg-white p-2 rounded-xl" />
                    <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                      <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow-lg" />
                    </div>
                  </button>
                  <div className="flex-1">
                    <div className="text-xs text-white/50 mb-1">{tr('mymouvTickets.number', 'N° du billet')}</div>
                    <div className="font-mono text-sm bg-black/40 rounded-lg px-3 py-2 select-all">{t.ticket_number}</div>
                    {t.scan_count > 0 && (
                      <div className={`mt-2 text-xs ${t.scan_count > 1 ? 'text-blue-300' : 'text-emerald-300'}`}>
                        ✓ {tr('mymouvTickets.scannedTimes', 'Scanné {n} fois').replace('{n}', t.scan_count)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen QR viewer — tap anywhere to close */}
      {qrFullscreen && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex flex-col items-center justify-center p-4"
          onClick={() => setQrFullscreen(null)}
          data-testid="mymouv-qr-fullscreen"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setQrFullscreen(null); }}
            className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md z-10"
            data-testid="mymouv-qr-fullscreen-close"
            aria-label={tr('common.close', 'Fermer')}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={qrFullscreen.src}
            alt="QR"
            className="bg-white p-6 rounded-3xl max-w-[90vw] max-h-[70vh] w-auto h-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="mymouv-qr-fullscreen-img"
          />
          <div className="mt-6 text-center">
            <div className="text-lg font-semibold text-white/90">{qrFullscreen.ticket?.event_title}</div>
            <div className="mt-1 font-mono text-sm text-white/60 bg-white/5 rounded-lg px-3 py-1 inline-block">{qrFullscreen.ticket?.ticket_number}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMouvMyTickets;
